import './device-form.scss';
import {ContentPage} from '../content-page/content-page';
import {useState, useEffect} from 'react';
import {useOffice} from '../../contexts/office/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {Device} from '../../backend/interfaces/device';
import {useHistory} from 'react-router-dom';
import {UsersRepository} from '../../repositories/users';
import {EntitiesRepository} from '../../repositories/entities';
import {Entity} from '../../backend/interfaces/entity';
import {Alert, Box, Button, Divider, Stack, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {PageHeader} from '../page-header/page-header';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {deviceTypes} from '../../utils/data/device-types';
import {userSignInTypes} from '../../utils/data/user-sign-in-types';
import {languages} from '../../utils/data/languages';
import {UserType} from '../../backend/enums/user-type';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {ScreenPaths} from '../../enums/screen-path';
import {PostDevice} from '../../backend/requests/post-device';
import {PutDevice} from '../../backend/requests/put-device';
import {User} from '../../backend/interfaces/user';
import {DevicesRepository} from '../../repositories/devices';
import {CaregiverMode} from '../../enums';
import {LoadingContent} from '../loading-content/loading-content';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {Modal} from '../modal/modal';
import {useFetchAllItems} from '../../hooks/fetch-all-items';

interface SearchSelectInputItem {
  id: string;
  name: string;
  picture?: string;
}

/**
 * Form to view, create, edit and delete a device.
 *
 * It uses the office context to get the device's information.
 *
 * Permissions are given by props.
 */
export const DeviceForm = ({
  canWrite,
  caregiverMode,
  ...props
}: {
  caregiverMode: CaregiverMode;
  canWrite: boolean;
  redirectUrl: string;
  showPageTitle: boolean;
}) => {
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();

  // Hold state
  const [originalModel, setOriginalModel] = useState<Device | undefined>();
  const [model, setModel] = useState<Device | undefined>();
  const [user, setUser] = useState<SearchSelectInputItem | undefined>();
  const [entity, setEntity] = useState<SearchSelectInputItem | undefined>();

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [serialNumberError, setSerialNumberError] = useState<string | undefined>();
  const [showSerialNumberError, setShowSerialNumberError] = useState<boolean>(false);

  // Buttons
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);

  // Dialogs
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  let history = useHistory();
  const {t} = useTranslation();

  // Set model when we get one
  useEffect(() => {
    // Set the model
    const init = async () => {
      if (officeContext.state.deviceForm) {
        // Init some state
        setDataChanged(false);
        setUser(undefined);
        setEntity(undefined);

        try {
          // Get the user
          if (officeContext.state.deviceForm.userId) {
            const userResult: User = await UsersRepository.instance.getById(
              officeContext.state.deviceForm.userId
            );

            if (userResult && userResult.id) {
              // Set user
              setUser({
                id: userResult.id,
                name: userResult.name,
                picture: userResult.picture,
              });
            } else {
              // Error
              throw new Error('Empty user.');
            }
          }

          // Get the entity
          if (officeContext.state.deviceForm.entityId) {
            const entityResult: Entity = await EntitiesRepository.instance.getById(
              officeContext.state.deviceForm.entityId
            );

            if (entityResult && entityResult.id) {
              // Set entity
              setEntity({
                id: entityResult.id,
                name: entityResult.name,
                picture: entityResult.picture,
              });
            } else {
              // Error
              throw new Error('Empty entity.');
            }
          }

          // Set models
          setModel(officeContext.state.deviceForm);
          setOriginalModel(officeContext.state.deviceForm);
        } catch (error) {
          // Log
          console.debug('🔴 Could not open the device.', error);

          // Redirect
          history.push(props.redirectUrl);
        }
      }
    };

    // Init
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.deviceForm]);

  // Form validation
  useEffect(() => {
    if (model && originalModel && canWrite) {
      // Hold validation
      let serialNumberValid: boolean = false;

      // Serial number validation
      if (
        (caregiverMode === CaregiverMode.SystemAdministrator &&
          model.serialNumber &&
          model.serialNumber.trim().length > 0) ||
        canWrite
      ) {
        // No errors
        if (serialNumberError !== undefined) {
          setSerialNumberError(undefined);
        }

        // Valid
        serialNumberValid = true;
      } else {
        // Error
        if (showSerialNumberError) {
          setSerialNumberError(
            model.id
              ? t(translations.device_form.errors.edit_form_validation_invalid_field)
              : t(translations.device_form.errors.create_form_validation_invalid_field)
          );
        }

        // Valid
        serialNumberValid = false;
      }

      // Data was updated
      let dataWasUpdated: boolean = false;

      // Check if the data was changed for the router prompt and the buttons validation
      if (
        (model.number || '').trim() !== (originalModel.number || '').trim() ||
        (model.macAddress || '').trim() !== (originalModel.macAddress || '').trim() ||
        (model.serialNumber || '').trim() !== (originalModel.serialNumber || '').trim() ||
        model.type !== originalModel.type ||
        model.isActive !== originalModel.isActive ||
        model.languageCode !== originalModel.languageCode ||
        model.userSignInType !== originalModel.userSignInType ||
        user?.id !== originalModel.userId ||
        entity?.id !== originalModel.entityId ||
        model.isInDebugMode !== originalModel.isInDebugMode
      ) {
        // Set it
        setDataChanged(true);

        // Set
        dataWasUpdated = true;
      } else {
        // Set it
        setDataChanged(false);

        // Set
        dataWasUpdated = false;
      }

      // Submit button validation
      if (canWrite && model.id) {
        if (serialNumberValid && dataWasUpdated) {
          // Enable submit button
          setSubmitButtonDisabled(false);
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
      } else if (caregiverMode === CaregiverMode.SystemAdministrator && !model.id) {
        if (serialNumberValid) {
          // Enable submit button
          setSubmitButtonDisabled(false);
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    model,
    originalModel,
    user,
    entity,
    showSerialNumberError,
    serialNumberError,
    canWrite,
    caregiverMode,
  ]);

  // Submit
  const handleSubmit = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !canWrite) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Disabled delete button
      setDeleteButtonDisabled(true);

      // Create or edit
      if (!model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Create <---

        // Request body
        const requestBody: PostDevice = {
          product: model.product,
          isActive: model.isActive,
          type: model.type,
          macAddress: model.macAddress?.trim() || undefined,
          serialNumber: model.serialNumber?.trim() || undefined,
          number: model.number?.trim() || undefined,
          languageCode: model.languageCode || undefined,
          userSignInType: model.userSignInType || undefined,
          userId: user?.id || undefined,
          entityId: entity?.id || undefined,
          isInDebugMode: model.isInDebugMode || undefined,
        };

        // Create
        try {
          const response = await DevicesRepository.instance.create(requestBody);
          officeContext.setDeviceForm(response);
          setSubmitDialogOpened(false);
          setIsSubmitting(false);
          setDeleteButtonDisabled(false);
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.device_form.success.create_message, {
              name: response.serialNumber || response.macAddress || '',
            }),
          });

          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}${response.id}`
              : `${props.redirectUrl}/${response.id}`
          );
        } catch (error) {
          console.error('Error creating device', error);
          setSubmitDialogOpened(false);
          setIsSubmitting(false);
          setDeleteButtonDisabled(false);
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_form.errors.create_message, {
              name: model.serialNumber || model.macAddress || '',
            }),
          });
        }
      } else if (model.id && caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Edit <---

        // Request body
        const requestBody: PutDevice = {
          product: model.product,
          isActive: model.isActive,
          type: model.type,
          macAddress: model.macAddress?.trim() || null,
          serialNumber: model.serialNumber?.trim() || null,
          number: model.number?.trim() || null,
          languageCode: model.languageCode || null,
          userSignInType: model.userSignInType || null,
          userId: user?.id || null,
          entityId: entity?.id || null,
          isInDebugMode: model.isInDebugMode || null,
        };

        try {
          // Edit
          const response = await DevicesRepository.instance.updateById(model.id, requestBody);

          // Reset context
          officeContext.setDeviceForm(response);

          setIsSubmitting(false);
          setSubmitDialogOpened(false);
          setDeleteButtonDisabled(false);
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.device_form.success.edit_message, {
              name: response.serialNumber || response.macAddress || '',
            }),
          });
        } catch (error) {
          console.error('Error editing device', error);
          setIsSubmitting(false);
          setSubmitDialogOpened(false);
          setDeleteButtonDisabled(false);
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_form.errors.edit_message, {
              name: model.serialNumber || model.macAddress || '',
            }),
          });
        }
      } else if (model.id && caregiverMode === CaregiverMode.Professional) {
        // ---> Edit <---

        // Request body
        const requestBody: PutDevice = {
          product: model.product,
          isActive: model.isActive,
          type: model.type,
          languageCode: model.languageCode || null,
          userId: user?.id || null,
          // Fields that can't be updated
          macAddress: undefined,
          serialNumber: undefined,
          number: undefined,
          userSignInType: undefined,
          entityId: undefined,
        };

        try {
          // Edit
          const response = await DevicesRepository.instance.updateById(model.id, requestBody);
          setIsSubmitting(false);
          setSubmitDialogOpened(false);
          setDeleteButtonDisabled(false);
          officeContext.setDeviceForm(response);
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.device_form.success.edit_message, {
              name: response.serialNumber || response.macAddress || '',
            }),
          });
        } catch (error) {
          console.error('Error editing device', error);
          setIsSubmitting(false);
          setSubmitDialogOpened(false);
          setDeleteButtonDisabled(false);
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_form.errors.edit_message, {
              name: model.serialNumber || model.macAddress || '',
            }),
          });
        }
      } else {
        // Error
        throw new Error('Missing permissions.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit/create the device.', error);

      setIsSubmitting(false);
      setSubmitDialogOpened(false);
      setDeleteButtonDisabled(false);
    }
  };

  // Delete device
  const handleDeleteDevice = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }
    setIsSubmitting(true);

    try {
      // Disable submit button
      setSubmitButtonDisabled(true);

      // Delete the device
      if (originalModel.id) {
        await DevicesRepository.instance.deleteById(originalModel.id);
      } else {
        // Error
        throw new Error('Empty device id.');
      }
      setDeleteDialogOpened(false);
      setIsSubmitting(false);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.device_form.success.deleted_message, {
          name: originalModel?.serialNumber || originalModel?.macAddress || ' ',
        }),
      });
      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated
      setTimeout(() => {
        // Navigate back
        history.replace(
          props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
            ? `${props.redirectUrl}?${ScreenPaths.DevicesDeletedQuery}=${
                originalModel?.serialNumber || originalModel?.macAddress || ' '
              }`
            : `${props.redirectUrl}/?${ScreenPaths.DevicesDeletedQuery}=${
                originalModel?.serialNumber || originalModel?.macAddress || ' '
              }`
        );
      }, 500);
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not delete the device.', error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.device_form.errors.deleted_message, {
          name: originalModel?.serialNumber || originalModel?.macAddress || ' ',
        }),
      });

      // Enable submit button again
      setSubmitButtonDisabled(false);
      setDeleteDialogOpened(false);
      setIsSubmitting(false);
    }
  };

  // Change select field
  const handleChangeSelectField = (
    value: string,
    field: 'userSignInType' | 'type' | 'languageCode' | 'isInDebugMode'
  ) => {
    if (field === 'type') {
      if (value) {
        setModel({
          ...model,
          [field]: value,
        } as Device);
      }
    } else {
      setModel({
        ...model,
        [field]: value || undefined,
      } as Device);
    }
  };

  // Change text field
  const handleChangeTextField = (
    value: string | undefined | null,
    field: 'number' | 'serialNumber' | 'macAddress'
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      [field]: finalValue,
    } as Device);
  };

  const {items: entities, isLoading: isLoadingEntities} = useFetchAllItems({
    fetchFunction: async ({limit, skip}) => {
      const response = await EntitiesRepository.instance.get(skip, limit);
      return {
        data: response.data.map((obj) => {
          return {
            id: obj.id || '',
            name: obj.name || '',
            avatar: obj.picture,
          };
        }),
        totalCount: response.totalCount,
      };
    },
  });

  const {items: users, isLoading} = useFetchAllItems({
    fetchFunction: async ({limit, skip}) => {
      if (!entity?.id) {
        return Promise.resolve({data: [], totalCount: 0});
      }
      const response = await UsersRepository.instance.getInEntity(
        entity.id,
        limit,
        skip,
        UserType.Senior,
        undefined,
        undefined,
        caregiverMode === CaregiverMode.SystemAdministrator ? undefined : 'activationStates=true'
      );

      return {
        data: response.data.map((obj, index) => {
          return {
            id: obj.user.id || '',
            name: obj.user.name || '',
            avatar: obj.user.picture,
          };
        }),
        totalCount: response.totalCount,
      };
    },
    deps: [entity?.id],
  });

  // Strings to use in the form
  let pageTitle: string = '';
  let saveDialogTitle: string = '';
  let saveDialogContent: string = '';
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';

  switch (caregiverMode) {
    case CaregiverMode.Professional:
      saveDialogTitle = t(translations.device_form.edit_dialog_title, {
        name: originalModel?.serialNumber || originalModel?.macAddress || '',
      });
      saveDialogContent = t(translations.device_form.edit_dialog_message);
      routerDialogTitle = t(translations.device_form.router_prompt_edit_title, {
        name: originalModel?.serialNumber || originalModel?.macAddress || '',
      });
      routerDialogContent = t(translations.device_form.router_prompt_edit_message);
      routerCancelToast = t(translations.device_form.edit_cancel_toast, {
        name: originalModel?.serialNumber || originalModel?.macAddress || '',
      });
      break;
    case CaregiverMode.SystemAdministrator:
      pageTitle = model?.id ? '' : t(translations.device_form.create_device_title);
      saveDialogTitle = model?.id
        ? t(translations.device_form.edit_dialog_title, {
            name: originalModel?.serialNumber || originalModel?.macAddress || '',
          })
        : t(translations.device_form.create_dialog_title, {
            name: model?.serialNumber,
          });
      saveDialogContent = model?.id
        ? t(translations.device_form.edit_dialog_message)
        : t(translations.device_form.create_dialog_message);
      routerDialogTitle = model?.id
        ? t(translations.device_form.router_prompt_edit_title, {
            name: originalModel?.serialNumber || originalModel?.macAddress || '',
          })
        : t(translations.device_form.router_prompt_create_title, {
            name: model?.serialNumber || model?.macAddress || '',
          });
      routerDialogContent = model?.id
        ? t(translations.device_form.router_prompt_edit_message)
        : t(translations.device_form.router_prompt_create_message);
      routerCancelToast = model?.id
        ? t(translations.device_form.edit_cancel_toast, {
            name: originalModel?.serialNumber || originalModel?.macAddress || '',
          })
        : t(translations.device_form.create_cancel_toast);
      break;
    default:
      break;
  }

  return (
    <ContentPage>
      {model ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitDialogOpened(true);
          }}
        >
          <Box
            className={`device-form-container ${
              !(props.showPageTitle || canWrite) ? 'device-form-container-without-page-header' : ''
            }`}
          >
            {(props.showPageTitle || canWrite) && (
              <PageHeader title={props.showPageTitle ? pageTitle : undefined} />
            )}
            <Box className="device-form-line-1-container">
              <Box className="device-form-line-1-left">
                <FlatSelectDropdown
                  label={t(translations.device_form.type_input)}
                  value={
                    model.type
                      ? {
                          key: model.type,
                          label: t(translations.device_types[model.type]) || model.type,
                        }
                      : undefined
                  }
                  onChange={(value) => handleChangeSelectField(value.key, 'type')}
                  options={deviceTypes.map((device) => {
                    return {key: device.type, label: t(device.translation)};
                  })}
                  disabled={!canWrite || caregiverMode !== CaregiverMode.SystemAdministrator}
                  required={caregiverMode === CaregiverMode.SystemAdministrator}
                  disabledTooltip={
                    !canWrite
                      ? t(translations.device_form.input_disabled_tooltip)
                      : caregiverMode === CaregiverMode.SystemAdministrator
                        ? undefined
                        : t(translations.device_form.edit_type_not_allowed_tooltip_title)
                  }
                />
              </Box>
              <Box className="device-form-line-1-right">
                <FlatSelectDropdown
                  label={t(translations.device_form.state_input)}
                  onChange={(value) =>
                    setModel({
                      ...model,
                      isActive: JSON.parse(value.key),
                    })
                  }
                  required
                  value={
                    model
                      ? {
                          key: model.isActive.toString(),
                          label: t(translations.device_form[`state_value_${model.isActive}`]),
                        }
                      : undefined
                  }
                  disabled={!canWrite}
                  disabledTooltip={
                    !canWrite ? t(translations.device_form.input_disabled_tooltip) : undefined
                  }
                  options={[
                    {key: 'true', label: t(translations.device_form.state_value_true)},
                    {key: 'false', label: t(translations.device_form.state_value_false)},
                  ]}
                />
              </Box>
            </Box>
            <Box className="device-form-line-2-container">
              <Box className="device-form-line-2-left">
                <FlatInput
                  label={t(translations.device_form.serial_number_input)}
                  disabled={!canWrite || caregiverMode !== CaregiverMode.SystemAdministrator}
                  value={model.serialNumber || ''}
                  disabledTooltip={
                    canWrite && caregiverMode !== CaregiverMode.SystemAdministrator
                      ? t(translations.device_form.edit_serial_number_not_allowed_tooltip_title)
                      : !canWrite
                        ? t(translations.device_form.input_disabled_tooltip)
                        : undefined
                  }
                  type="text"
                  required={caregiverMode === CaregiverMode.SystemAdministrator}
                  onChange={(event) => handleChangeTextField(event.target.value, 'serialNumber')}
                  onBlur={() => {
                    if (!showSerialNumberError) {
                      setShowSerialNumberError(true);
                    }
                  }}
                  errors={serialNumberError}
                />
              </Box>
              <Box className="device-form-line-2-right">
                <FlatInput
                  label={t(translations.device_form.mac_address_input)}
                  disabled={!canWrite || caregiverMode !== CaregiverMode.SystemAdministrator}
                  value={model.macAddress || ''}
                  disabledTooltip={
                    canWrite && caregiverMode !== CaregiverMode.SystemAdministrator
                      ? t(translations.device_form.edit_mac_address_not_allowed_tooltip_title)
                      : !canWrite
                        ? t(translations.device_form.input_disabled_tooltip)
                        : undefined
                  }
                  type="text"
                  onChange={(event) => handleChangeTextField(event.target.value, 'macAddress')}
                />
              </Box>
            </Box>
            <Box className="device-form-line-2-container">
              <Box className="device-form-line-2-left">
                <FlatInput
                  label={t(translations.device_form.number_input)}
                  value={model.number || ''}
                  type="text"
                  disabled={!canWrite || caregiverMode !== CaregiverMode.SystemAdministrator}
                  disabledTooltip={
                    !canWrite
                      ? t(translations.device_form.input_disabled_tooltip)
                      : caregiverMode !== CaregiverMode.SystemAdministrator
                        ? t(translations.device_form.edit_number_not_allowed_tooltip_title)
                        : undefined
                  }
                  onChange={(event) => handleChangeTextField(event.target.value, 'number')}
                />
              </Box>
            </Box>
            {caregiverMode === CaregiverMode.SystemAdministrator && (
              <Box className="device-form-line-4-container">
                <FlatSelectDropdown
                  label={t(translations.device_form.entity_input)}
                  loading={isLoadingEntities}
                  role="user"
                  clearable
                  value={
                    entity
                      ? {
                          key: entity.id,
                          label: entity.name,
                          picture: entity.picture,
                        }
                      : undefined
                  }
                  options={
                    !entities
                      ? []
                      : entities.map((obj) => {
                          return {
                            key: obj.id,
                            label: obj.name,
                            picture: obj.avatar,
                          };
                        })
                  }
                  onChange={(value) => {
                    if (!value.key) {
                      setEntity(undefined);
                      setUser(undefined);
                      setModel({
                        ...model,
                        entityId: undefined,
                      } as Device);
                    } else {
                      if (value.key === entity?.id || !entities) {
                        return;
                      }
                      setUser(undefined);
                      const find = entities.find((entity) => entity.id === value.key);
                      if (find) {
                        setEntity({
                          id: find.id,
                          name: find.name,
                          picture: find.avatar,
                        });
                        setModel({
                          ...model,
                          entityId: find.id,
                        } as Device);
                      }
                    }
                  }}
                />
              </Box>
            )}
            <Box className="device-form-line-5-container">
              <FlatSelectDropdown
                label={t(translations.device_form.user_input)}
                role="user"
                disabled={!canWrite}
                disabledTooltip={
                  !canWrite ? t(translations.device_form.input_disabled_tooltip) : undefined
                }
                value={
                  user
                    ? {
                        key: user.id,
                        label: user.name,
                        picture: user.picture,
                      }
                    : undefined
                }
                loading={isLoading}
                options={
                  !users
                    ? []
                    : users.map((obj) => {
                        return {
                          key: obj.id,
                          label: obj.name,
                          picture: obj.avatar,
                        };
                      })
                }
                clearable
                onChange={(value) => {
                  if (!value.key) {
                    setUser(undefined);
                    setModel({
                      ...model,
                      userId: undefined,
                    } as Device);
                  } else if (value.key && users) {
                    if (value.key === user?.id) return;
                    const find = users.find((user) => user.id === value.key);
                    if (find)
                      setUser({
                        id: find.id,
                        name: find.name,
                        picture: find.avatar,
                      });
                    setModel({
                      ...model,
                      userId: value?.key,
                    } as Device);
                  }
                }}
              />
            </Box>
            {caregiverMode === CaregiverMode.SystemAdministrator && (
              <Alert severity="info" className="device-form-user-alert ">
                {t(translations.device_form.user_info_message)}
              </Alert>
            )}
            <Box className="device-form-line-6-container">
              <Box className="device-form-line-6-left">
                <FlatSelectDropdown
                  label={t(translations.device_form.sign_in_type_input)}
                  placeholder={t(translations.device_form.sign_in_type_placeholder_input)}
                  onChange={(value) => handleChangeSelectField(value.key, 'userSignInType')}
                  value={
                    model.userSignInType
                      ? {
                          key: model.userSignInType.toString(),
                          label: t(translations.user_sign_in_types[model.userSignInType]) || '',
                        }
                      : undefined
                  }
                  disabled={!canWrite || caregiverMode !== CaregiverMode.SystemAdministrator}
                  disabledTooltip={
                    !canWrite
                      ? t(translations.device_form.input_disabled_tooltip)
                      : caregiverMode === CaregiverMode.SystemAdministrator
                        ? undefined
                        : t(translations.device_form.edit_sign_in_type_not_allowed_tooltip_title)
                  }
                  options={userSignInTypes.map((obj) => {
                    return {
                      key: obj.type,
                      label: t(obj.translation),
                    };
                  })}
                  clearable
                />
              </Box>
              <Box className="device-form-line-6-right">
                <FlatSelectDropdown
                  label={t(translations.device_form.language_input)}
                  placeholder={t(translations.device_form.language_placeholder_input)}
                  onChange={(value) => handleChangeSelectField(value.key, 'languageCode')}
                  value={
                    model.languageCode
                      ? {
                          key: model.languageCode,
                          label: t(translations.languages[model.languageCode]) || '',
                        }
                      : undefined
                  }
                  disabled={!canWrite}
                  disabledTooltip={
                    !canWrite ? t(translations.device_form.input_disabled_tooltip) : undefined
                  }
                  options={languages.map((obj) => {
                    return {
                      key: obj.type,
                      label: t(obj.translation),
                    };
                  })}
                  clearable
                />
              </Box>
            </Box>
            <Box className="device-form-line-7-container">
              {caregiverMode === CaregiverMode.SystemAdministrator && (
                <Box className="device-form-line-2-left">
                  <FlatSelectDropdown
                    label={t(translations.device_form.isInDebugMode_input)}
                    placeholder={t(translations.device_form.isInDebugMode_placeholder_input)}
                    value={
                      model.isInDebugMode
                        ? {
                            key: model.isInDebugMode.toString(),
                            label:
                              t(translations.device_form[`state_value_${model.isInDebugMode}`]) ||
                              '',
                          }
                        : undefined
                    }
                    options={[
                      {key: 'true', label: t(translations.device_form.state_value_true)},
                      {key: 'false', label: t(translations.device_form.state_value_false)},
                    ]}
                    onChange={(value) => handleChangeSelectField(value.key, 'isInDebugMode')}
                    clearable
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Stack
            className="device-form-footer"
            flexDirection="row"
            gap="10px"
            justifyContent="space-between"
          >
            <Button
              variant="text"
              color="error"
              sx={{
                width: '100%',
                height: '40px',
                padding: '8px 16px',
                borderRadius: '5px',
                maxWidth: '252px',
              }}
              onClick={() => {
                history.replace(props.redirectUrl);
              }}
              disabled={isSubmitting}
            >
              {t(translations.user_form.cancel_button)}
            </Button>
            <LoadingButton
              sx={{
                width: '100%',
                maxWidth: '252px',
              }}
              variant="contained"
              loading={isSubmitting}
              loadingPosition={isSubmitting ? 'start' : undefined}
              disabled={submitButtonDisabled || isSubmitting}
              type="submit"
            >
              {t(translations.device_form.save_button)}
            </LoadingButton>
          </Stack>
        </form>
      ) : (
        <LoadingContent />
      )}

      {/* Delete button and dialog */}
      {model && model.id && caregiverMode === CaregiverMode.SystemAdministrator && (
        <>
          <Box className="device-form-delete-container">
            <Divider></Divider>
            <Box className="device-form-delete-button-container">
              <Button
                sx={{
                  width: '100%',
                  maxWidth: '252px',
                }}
                variant="outlined"
                color="error"
                disabled={deleteButtonDisabled || isSubmitting}
                onClick={(event: any) => {
                  event.preventDefault();
                  setDeleteDialogOpened(true);
                }}
              >
                {t(translations.device_form.delete_button)}
              </Button>
            </Box>
          </Box>
          <Modal
            open={deleteDialogOpened}
            maxWidth="sm"
            fullWidth
            className="device-form-modal"
            preventCloseClick
            onClose={() => setDeleteDialogOpened(false)}
            actions={[
              {
                label: t(translations.modal.cancel),
                type: 'destructive',
                onClick: () => setDeleteDialogOpened(false),
                disabled: isSubmitting,
              },
              {
                label: t(translations.modal.delete),
                type: 'nondestructive',
                onClick: handleDeleteDevice,
                loading: isSubmitting,
                disabled: isSubmitting,
              },
            ]}
          >
            <Typography className="title">
              {t(translations.device_form.delete_dialog_title, {
                name: originalModel?.serialNumber || originalModel?.macAddress || '',
              })}
            </Typography>
            <Typography className="subtitle">
              {t(translations.device_form.delete_dialog_content)}
            </Typography>
          </Modal>
        </>
      )}
      {/* Save dialog */}
      <Modal
        open={submitDialogOpened}
        maxWidth="sm"
        fullWidth
        className="device-form-modal"
        preventCloseClick
        onClose={() => setSubmitDialogOpened(false)}
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: () => setSubmitDialogOpened(false),
            disabled: isSubmitting,
          },
          {
            label: t(translations.device_form.save_button),
            type: 'nondestructive',
            onClick: handleSubmit,
            loading: isSubmitting,
            disabled: isSubmitting,
          },
        ]}
      >
        <Typography className="title">{saveDialogTitle}</Typography>
        <Typography className="subtitle">{saveDialogContent}</Typography>
      </Modal>

      {/* Router */}
      {model && canWrite && (
        <RouterPrompt
          // Show prompt if the data changed but make sure the path
          // is not changing because the device was deleted/created
          when={dataChanged && !isSubmitting}
          subtitle={routerDialogTitle}
          message={routerDialogContent}
          onOK={() => true}
          onCancel={() => false}
          toastNotification={{
            severity: 'info',
            message: routerCancelToast,
          }}
        />
      )}
    </ContentPage>
  );
};
